import React, { useContext, useEffect, useState } from 'react';
import { DeleteOutlined, EyeOutlined, FileTextOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import {
    Button,
    ConfigProvider,
    Form,
    Input,
    message,
    Modal,
    Space,
    Table,
    TableProps,
    Tag,
    Collapse,
    Tabs,
    Typography,
    Popconfirm,
    Tooltip,
    Alert
} from 'antd';
import locale from "antd/es/locale/es_ES";
import { AuthContext } from "../../../../config/AuthContext";
import { GetJutgeProblemesIdEstadistiques } from "../../../../dao/jutge/GetJutgeProblemesIdEstadistiques";
import { DeleteJutgeEnviament } from "../../../../dao/jutge/DeleteJutgeEnviament";
import Editor from "@monaco-editor/react";
import TestsListModal from "./TestsListModal";
import { ColumnsType } from "antd/es/table";
import JutgeColors from "../../../../styles/JutgeColors";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { GetJutgeProblemesIdRanking } from "../../../../dao/jutge/GetJutgeProblemesIdRanking";
import { motion } from "framer-motion";
import ProblemaIdEnunciat from "../../problemesId/content/ProblemaIdEnunciat";
import ProblemaIdExemples from "../../problemesId/content/ProblemaIdExemples";
import ModoDesafioTag from "./ModoDesafioTag";

const { Panel } = Collapse;
const { Text } = Typography;

interface Props {
    element: any;
    visible: boolean;
    onClose: (value: boolean) => void;
    editar?: boolean;
}

interface TestResult {
    ordre: number;
    estat: number;
    expected: string;
    actual: string;
    errors: string;
}

interface FileData {
    uid: string;
    nom: string;
    fitxer: string; // Base64
}

interface EnviamentData {
    id_enviament: number;
    moment: string;
    files: string; // JSON string
    tests: string; // JSON string
    // Parsed data
    parsedFiles?: FileData[];
    parsedTests?: TestResult[];
    parsedJunit?: JunitData[];
}

interface JunitData {
    id: number;
    tipus: string;
    ordre: number;
    fitxer: string;
    moment: string;
    name: string;
}

const RevisioModal: React.FC<Props> = (props: Props) => {
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [showTestsModal, setShowTestsModal] = useState(false);
    const [showEnunciatModal, setShowEnunciatModal] = useState(false);
    const [dades, setDades] = useState<EnviamentData[]>([]);
    const [data, setData] = useState<any>();
    const [ranking, setRanking] = useState<any[]>([]);
    const [errorOutput, setErrorOutput] = useState<string | null>(null);

    // Editor state (Multi-file)
    const [filesToEdit, setFilesToEdit] = useState<{ uid: string; nom: string; content: string }[]>([]);
    const [activeFileKey, setActiveFileKey] = useState<string>("");

    const [selectedEnviament, setSelectedEnviament] = useState<number | null>(null);

    const { isLoggedIn, user } = useContext(AuthContext);
    const isEditable = user?.tipus === "0" || user?.tipus === "1";

    const [activeKeys, setActiveKeys] = useState<string | string[]>([]);

    const isAllCorrect = (env: EnviamentData) => {
        if (!env.parsedTests || env.parsedTests.length === 0) return false;
        return env.parsedTests.every(test => test.estat === 1);
    }

    // Use URL from environment
    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (isLoggedIn && props.visible) {
            checkDades();
        }
    }, [props.visible]);

    const deleteEnviament = async (id: number) => {
        try {
            const response = await DeleteJutgeEnviament(id.toString());
            if (response.ok) {
                message.success("Enviament eliminat correctament");
                checkDades();
            } else {
                message.error("Error al eliminar l'enviament");
            }
        } catch (e) {
            console.error(e);
            message.error("Error de connexió");
        }
    };

    const decodeFileContent = (b64: string) => {
        try {
            return atob(b64);
        } catch (e) {
            return "";
        }
    }

    async function checkDades() {
        setLoading(true);
        // We use the same stats endpoint
        const rawData = await GetJutgeProblemesIdEstadistiques(
            props.element.id + "",
            props.element.id_usuari,
            props.element.id_practica || "0"
        );
        setData(props.element);

        let parsedData: EnviamentData[] = [];
        try {
            let temp = rawData;
            if (typeof rawData === 'string') {
                temp = JSON.parse(rawData);
            } else if (rawData && rawData.json) {
                temp = JSON.parse(rawData.json);
            }

            if (Array.isArray(temp)) {
                parsedData = temp
                    .filter((d: any) => d && d.moment)
                    .map((d: any) => ({
                        ...d,
                        parsedFiles: d.files
                            ? (typeof d.files === 'string' ? JSON.parse(d.files) : d.files)
                            : [],
                        parsedTests: (d.tests
                            ? (typeof d.tests === 'string' ? JSON.parse(d.tests) : d.tests)
                            : []).sort((a: any, b: any) => a.ordre - b.ordre),
                        parsedJunit: d.junit
                            ? (typeof d.junit === 'string' ? JSON.parse(d.junit) : d.junit)
                            : []
                    }))
                    .sort((a: EnviamentData, b: EnviamentData) => b.id_enviament - a.id_enviament);

                setDades(parsedData);


                // Set default code from latest enviament
                if (parsedData.length > 0) {
                    const latest = parsedData[0];
                    setActiveKeys([latest.id_enviament.toString()]);
                    setSelectedEnviament(latest.id_enviament);
                    loadFilesFromEnviament(latest);
                }
            } else {
                setDades([]);
            }
        } catch (e) {
            console.error("Error parsing stats", e);
            setDades([]);
        }
        setLoading(false);

        // Fetch ranking data
        try {
            const r: any = await GetJutgeProblemesIdRanking(
                props.element.id + "",
                props.element.id_practica || "0"
            );
            setRanking(Array.isArray(r) ? r : []);
        } catch (e) {
            console.error("Error fetching ranking", e);
        }
    }

    const loadFilesFromEnviament = (env: EnviamentData) => {
        if (env.parsedFiles && env.parsedFiles.length > 0) {
            const mapped = env.parsedFiles.map(f => ({
                uid: f.uid,
                nom: f.nom,
                content: decodeFileContent(f.fitxer)
            }));
            setFilesToEdit(mapped);
            // If current active key is not in new files or empty, set to first
            if (!activeFileKey || !mapped.find(f => f.uid === activeFileKey)) {
                setActiveFileKey(mapped[0].uid);
            }
        } else {
            setFilesToEdit([]);
        }
    }

    // Estat per crear un nou fitxer o canviar el nom de la classe
    const [newFileNameInput, setNewFileNameInput] = useState<string>("");
    const [renameModalVisible, setRenameModalVisible] = useState(false);
    const [renamingUid, setRenamingUid] = useState<string | null>(null);
    const [addFileModalVisible, setAddFileModalVisible] = useState(false);

    const handleCreateNewSubmission = () => {
        setSelectedEnviament(null);
        setActiveKeys([]);
        const defaultClass = "Main";
        const initialFile = {
            uid: `new_${Date.now()}`,
            nom: `${defaultClass}.java`,
            content: `public class ${defaultClass} {\n    public static void main(String[] args) {\n        \n    }\n}\n`
        };
        setFilesToEdit([initialFile]);
        setActiveFileKey(initialFile.uid);
        message.info("Nou enviament preparat. Edita el nom de la classe o el codi i clica Reenviar.");
    };

    const handleConfirmAddFile = () => {
        let name = newFileNameInput.trim();
        if (!name) {
            message.warning("Introdueix el nom de la classe");
            return;
        }
        if (!name.endsWith(".java")) {
            name = `${name}.java`;
        }
        const className = name.replace(/\.java$/, "");
        const newFile = {
            uid: `file_${Date.now()}`,
            nom: name,
            content: `public class ${className} {\n    \n}\n`
        };
        setFilesToEdit(prev => [...prev, newFile]);
        setActiveFileKey(newFile.uid);
        setNewFileNameInput("");
        setAddFileModalVisible(false);
    };

    const handleConfirmRename = () => {
        let name = newFileNameInput.trim();
        if (!name || !renamingUid) return;
        if (!name.endsWith(".java")) {
            name = `${name}.java`;
        }
        setFilesToEdit(prev => prev.map(f => f.uid === renamingUid ? { ...f, nom: name } : f));
        setNewFileNameInput("");
        setRenamingUid(null);
        setRenameModalVisible(false);
    };

    const handleCancel = () => props.onClose(false);

    // 🚀 Reenviar código (Multi-file logic)
    const handleSend = async () => {
        if (filesToEdit.length === 0) {
            message.error("Has d'afegir com a mínim un fitxer .java.");
            return;
        }

        setLoading(true);
        setErrorOutput(null);
        try {
            // Prepare files payload
            const filesPayload = filesToEdit.map(f => ({
                nom: f.nom,
                content: f.content
            }));

            // We use the first file name as primary filename for backward compatibility / logging
            const primaryFile = filesToEdit.length > 0 ? filesToEdit[0] : { nom: "Main.java", content: "" };

            const body = {
                idEnviamentOrigin: selectedEnviament || 0,
                filename: primaryFile.nom,
                content: primaryFile.content,
                files: filesPayload
            };

            const response = await fetch(
                `${apiUrl}:8061/jutge/activitats/${props.element.id_practica || "0"}/problemes/${props.element.id}/usuaris/${props.element.id_usuari}/reenviar`,
                {
                    method: "POST",
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                }
            );

            if (response.ok) {
                message.success(selectedEnviament ? "Codi reenviat i testejat correctament!" : "Nou enviament generat i testejat correctament!");
                await checkDades();
            } else {
                let errorMsg = "Error en processar l'enviament.";
                try {
                    const errData = await response.json();
                    if (errData?.response) errorMsg = errData.response;
                    else if (errData?.message) errorMsg = errData.message;
                    else if (typeof errData === 'string') errorMsg = errData;
                } catch (_) {}
                message.error({ content: errorMsg, duration: 10 });
                setErrorOutput(errorMsg);
            }

        } catch (err) {
            console.error(err);
            message.error("Error durant l'enviament.");
        }
        setLoading(false);
    };

    const handleEditorChange = (value: string | undefined, uid: string) => {
        setFilesToEdit(prev => prev.map(f => f.uid === uid ? { ...f, content: value || "" } : f));
    };

    const renderJunitVisual = (junitTest: JunitData | undefined, content: string) => {
        if (!junitTest) return <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap', maxHeight: '150px', overflow: 'auto' }}>{content}</pre>;

        try {
            const tipus = junitTest.tipus;
            const fallback = <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap', maxHeight: '150px', overflow: 'auto' }}>{content}</pre>;

            if (["method", "class", "content_class", "exception_check", "io_file"].includes(tipus)) {
                let data: any;
                try { data = JSON.parse(content); } catch (e) { return fallback; }

                if (tipus === "method") {
                    return (
                        <div style={{ fontSize: '0.75rem' }}>
                            <p style={{ marginBottom: 2 }}><b>Mètode:</b> {data.methodName}</p>
                            <p style={{ marginBottom: 2 }}><b>Modificadors: </b>
                                {data.modifiers && data.modifiers.length > 0 ? (
                                    data.modifiers.map((m: string, idx: number) => (
                                        <Tag key={idx} color="purple" style={{ marginRight: 2 }}>{m}</Tag>
                                    ))
                                ) : (
                                    <Tag color="red" style={{ marginRight: 2 }}>Sense modificadors</Tag>
                                )}
                            </p>
                            <p style={{ marginBottom: 2 }}><b>Retorn:</b> <Tag color={JutgeColors.colors.color3}>{data.returnType}</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Paràmetres:  </b>
                                {data.parameters && data.parameters.length > 0 ? (
                                    data.parameters.map((p: any, idx: number) => (
                                        <React.Fragment key={idx}>
                                            <Tag color={JutgeColors.colors.color4} style={{ marginRight: 2 }}>{p.paramName}</Tag>
                                            {"-->"}
                                            <Tag color={JutgeColors.colors.color2} style={{ marginLeft: 2, marginRight: 2 }}>{p.paramType}</Tag>
                                            {"  ,  "}
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <Tag color="red" >Sense paràmetres</Tag>
                                )}
                            </p>
                        </div>
                    );
                }

                if (tipus === "class") {
                    return (
                        <div style={{ fontSize: '0.75rem' }}>
                            <div style={{ marginBottom: 8 }}>
                                {data.modifiers && data.modifiers.map((m: string, idx: number) => (
                                    <Tag key={idx} color="purple" style={{ marginRight: 2 }}>{m}</Tag>
                                ))}
                                <Tag color="magenta" style={{ marginRight: 2 }}>{data.classType}</Tag>
                                <span style={{ fontWeight: 'bold', marginRight: 8, color: '#1677ff' }}>{data.className}</span>

                                {data.extendedClass && (
                                    <span>
                                        <span style={{ color: '#999', marginRight: 4 }}>extends</span>
                                        <Tag color="orange" style={{ marginRight: 2 }}>{data.extendedClass}</Tag>
                                    </span>
                                )}

                                {data.implementedInterfaces && data.implementedInterfaces.length > 0 && (
                                    <span>
                                        <span style={{ color: '#999', margin: '0 4px' }}>implements</span>
                                        {data.implementedInterfaces.map((i: string, idx: number) => <Tag key={idx} color="cyan" style={{ marginRight: 2 }}>{i}</Tag>)}
                                    </span>
                                )}
                            </div>

                            {data.classType === 'enum' && data.enumValues && data.enumValues.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <b>Valors Enum:</b>
                                    <div style={{ paddingLeft: 10, marginTop: 4 }}>
                                        {data.enumValues.map((val: string, idx: number) => (
                                            <Tag key={idx} color="gold" style={{ marginRight: 2 }}>{val}</Tag>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {data.constructors && data.constructors.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <b>Constructors:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {data.constructors.map((c: any, idx: number) => (
                                            <li key={idx}>
                                                {c.modifiers && c.modifiers.map((mod: string, i: number) => <span key={i} style={{ color: '#999', fontSize: '0.9em' }}>{mod} </span>)}
                                                <b>{data.className}</b>(
                                                {c.parameters && c.parameters.map((p: any, pi: number) => (
                                                    <span key={pi}>
                                                        {p.paramType} {p.paramName}{pi < c.parameters.length - 1 ? ', ' : ''}
                                                    </span>
                                                ))}
                                                )
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {data.fields && data.fields.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <b>Atributs:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {data.fields.map((f: any, idx: number) => (
                                            <li key={idx}>
                                                {f.modifiers && f.modifiers.map((mod: string, i: number) => <span key={i} style={{ color: '#999', fontSize: '0.9em' }}>{mod} </span>)}
                                                <Tag color={JutgeColors.colors.color4} style={{ marginRight: 2 }}>{f.fieldName}</Tag>
                                                : <Tag color={JutgeColors.colors.color3} style={{ marginLeft: 2, marginRight: 2 }}>{f.fieldType}</Tag>
                                                {f.checkGetter && <Tag color="green" style={{ fontSize: '0.8em', marginRight: 2 }}>G</Tag>}
                                                {f.checkSetter && <Tag color="blue" style={{ fontSize: '0.8em' }}>S</Tag>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {data.methods && data.methods.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <b>Mètodes:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {data.methods.map((m: any, idx: number) => (
                                            <li key={idx}>
                                                {m.modifiers && m.modifiers.map((mod: string, i: number) => <span key={i} style={{ color: '#999', fontSize: '0.9em' }}>{mod} </span>)}
                                                <Tag color={JutgeColors.colors.color3} style={{ marginRight: 2 }}>{m.returnType}</Tag>
                                                <b>{m.methodName}</b>(
                                                {m.parameters && m.parameters.map((p: any, pi: number) => (
                                                    <span key={pi}>
                                                        {p.paramType} {p.paramName}{pi < m.parameters.length - 1 ? ', ' : ''}
                                                    </span>
                                                ))}
                                                )
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    );
                }

                if (tipus === "content_class") {
                    return (
                        <div style={{ fontSize: '0.75rem' }}>
                            <p style={{ marginBottom: 2 }}><b>Contingut en Clase:</b> <Tag color="geekblue">{data.refClassName || "Unknown"}</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Text a buscar:</b> <Tag>{data.searchText}</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Condició:</b> {data.mustContain ? <Tag color="green">Ha de contenir</Tag> : <Tag color="red">No ha de contenir</Tag>}</p>
                        </div>
                    );
                }

                if (tipus === "exception_check") {
                    return (
                        <div style={{ fontSize: '0.75rem' }}>
                            <p style={{ marginBottom: 2 }}><b>Excepció en:</b> <Tag color="geekblue">{data.refClassName || "?"}.{data.methodName}</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Excepció Esperada:</b> <Tag color="red">{data.exceptionType}</Tag></p>
                            {data.expectedOutput && (
                                <p style={{ marginBottom: 2 }}><b>Sortida Esperada (Missatge):</b> <br />
                                    <Tag color="orange" style={{ whiteSpace: 'pre-wrap' }}>{data.expectedOutput}</Tag>
                                </p>
                            )}
                        </div>
                    );
                }

                if (tipus === "io_file") {
                    return (
                        <div style={{ fontSize: '0.75rem' }}>
                            <p style={{ marginBottom: 2 }}><b>Entrada per Fitxer:</b> <Tag color="blue">Input File</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Tipus Sortida:</b> <Tag>{data.outputType || "text"}</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Sortida Esperada:</b> <Tag color="orange" style={{ whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'auto', display: 'block' }}>{data.output || data.outputContent || "Sense sortida definida"}</Tag></p>
                        </div>
                    );
                }
            }

            return fallback;
        } catch (e) {
            return <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap', maxHeight: '150px', overflow: 'auto' }}>{content}</pre>;
        }
    }

    const getColumns = (currentEnv: EnviamentData): ColumnsType<TestResult> => [
        {
            title: 'Test',
            dataIndex: 'ordre',
            key: 'ordre',
            width: 80,
            render: (text) => <Tag color={JutgeColors.colors.color3}>#{text}</Tag>
        },
        {
            title: 'Estat',
            dataIndex: 'estat',
            key: 'estat',
            width: 100,
            render: (estat) => (
                <Tag color={estat === 1 ? JutgeColors.colors.color4 : JutgeColors.colors.color1}>
                    {estat === 1 ? "CORRECTE" : "INCORRECTE"}
                </Tag>
            )
        },
        {
            title: 'Entrada',
            key: 'entrada',
            width: 300,
            render: (_, record) => {
                if (user.tipus !== "1" && user.tipus !== "0") return <Text disabled italic>Ocult</Text>;

                const junitTest = currentEnv.parsedJunit?.find(j => j.ordre === record.ordre);

                // If passed, maybe we don't show input to save space if requested? 
                // But typically input is useful. The user asked "solamente muestralo si hay error" previously.
                // But in a dedicated column, blank rows look weird. 
                // I will show it always if available for admins.
                // However, I will respect "solamente si hay error" implicitly if I wanted, but the new prompt asks for a COLUMN "instead of scrolling".

                let junitContent = "";
                if (junitTest && junitTest.fitxer) {
                    const val = junitTest.fitxer;
                    let isJson = false;
                    try {
                        const dj = JSON.parse(val);
                        if (dj && typeof dj === 'object') isJson = true;
                    } catch (e) { }

                    if (!isJson && val.includes(":")) {
                        const parts = val.split(":");
                        if (parts.length > 2) {
                            try { junitContent = atob(parts[2]); } catch (e) { }
                        }
                    }

                    if (!junitContent && !isJson) {
                        try { junitContent = atob(val); } catch (e) { junitContent = val; }
                    } else if (!junitContent) {
                        junitContent = val;
                    }
                }

                if (!junitContent) return <Text type="secondary">-</Text>;

                return (
                    <div style={{ maxHeight: '150px', overflow: 'auto' }}>
                        {renderJunitVisual(junitTest, junitContent)}
                    </div>
                );
            }
        },
        {
            title: 'Errors / Detalls',
            key: 'details',
            render: (_, record) => {
                if (record.estat === 1) return <Text type="success">OK</Text>;

                if (record.errors === "exception") {
                    return (
                        <Tooltip
                            title={<pre style={{ whiteSpace: 'pre-wrap', maxHeight: '400px', overflow: 'auto' }}>{record.actual}</pre>}
                            trigger="hover"
                            overlayStyle={{ maxWidth: '600px' }}
                        >
                            <Tag color="red" style={{ cursor: 'pointer' }}>EXCEPTION</Tag>
                        </Tooltip>
                    );
                }

                return (
                    <div style={{ maxHeight: '100px', overflow: 'auto', fontSize: '0.85em' }}>
                        {(user.tipus === "1" || user.tipus === "0") && record.errors && <pre style={{ margin: 0, color: 'red' }}>{record.errors}</pre>}
                    </div>
                );
            }
        }
    ];

    const renderPodium = () => {
        if (!Array.isArray(ranking) || ranking.length === 0) return null;

        // El podium solo aplica si la solución se ha hecho en Modo Desafío (1) o en el juego (2)
        const isDesafioOrGame = props.element?.origen_success === 1 || props.element?.origen_success === 2 ||
                                props.element?.origen_success === "1" || props.element?.origen_success === "2";
        if (!isDesafioOrGame) return null;

        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-end",
                gap: "16px",
                marginBottom: "24px",
                minHeight: "150px",
                background: '#f9f9f9',
                padding: '20px',
                borderRadius: '8px'
            }}>
                {ranking.map((r, idx) => (
                    <motion.div
                        key={`${r.position}-${idx}`}
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.5, delay: idx * 0.2 }}
                        style={{
                            backgroundColor: r.color ?? "#ccc",
                            width: 120,
                            height: 120,
                            borderRadius: "12px 12px 0 0",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            padding: "8px",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.2)"
                        }}
                    >
                        <div style={{ fontWeight: "bold", textAlign: "center" }}>{r.nom}</div>
                        <div style={{ fontWeight: "bold", textAlign: "center", marginTop: "2px" }}>{r.puntos} pts</div>
                        <div style={{ fontSize: "0.7rem", color: "#333", marginTop: "2px" }}>{r.position}</div>
                    </motion.div>
                ))}
            </div>
        );
    };

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span>Historial i Re-Enviament</span>
                        {props.element?.progres === 1 && (
                            <ModoDesafioTag origenSuccess={props.element?.origen_success} />
                        )}
                    </div>
                }
                open={props.visible}
                onCancel={handleCancel}
                footer={null} // Custom footer inside content or none needed if using inline actions
                width={1400} // Made slightly wider for split view
            >
                <div style={{ display: 'flex', gap: '20px', height: '80vh' }}>

                    {/* LEFT: List of Enviaments */}
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {renderPodium()}
                        <h4>Historial</h4>
                        <Collapse
                            accordion
                            activeKey={activeKeys}
                            onChange={(keys) => {
                                setActiveKeys(keys);
                                const key = Array.isArray(keys) ? keys[0] : keys;
                                if (key) {
                                    const envId = Number(key);
                                    setSelectedEnviament(envId);
                                    const env = dades.find(d => d.id_enviament === envId);
                                    if (env) loadFilesFromEnviament(env);
                                }
                            }}
                        >
                            {dades.map(env => {
                                const allCorrect = isAllCorrect(env);
                                const bgColor = allCorrect ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)';

                                return (
                                    <Panel
                                        style={{ background: bgColor, marginBottom: 5, borderRadius: 4, border: 'none' }}
                                        header={
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span>#{env.id_enviament} - {env.moment} {allCorrect ? <Tag color="green">PASSED</Tag> : <Tag color="red">FAILED</Tag>}
                                                    {" "}<ModoDesafioTag record={env} hideWhenPending />
                                                </span>
                                                {(user.tipus === "1" || user.tipus === "0") && (
                                                    <div onClick={e => e.stopPropagation()}>
                                                        <Popconfirm
                                                            title="Eliminar enviament?"
                                                            description="Això eliminarà l'enviament, els fitxers i els resultats."
                                                            onConfirm={() => deleteEnviament(env.id_enviament)}
                                                            okText="Sí"
                                                            cancelText="No"
                                                        >
                                                            <Button type="text" danger icon={<DeleteOutlined />} />
                                                        </Popconfirm>
                                                    </div>
                                                )}
                                            </div>
                                        } key={env.id_enviament}>

                                        <Table dataSource={env.parsedTests} columns={getColumns(env)} pagination={false} size="small" rowKey="ordre" />

                                    </Panel>
                                )
                            })}
                        </Collapse>
                    </div>

                    {/* RIGHT: Editor for Re-submission (or View Only) */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderLeft: '1px solid #f0f0f0', paddingLeft: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                            <h4 style={{ margin: 0 }}>{isEditable ? (selectedEnviament ? `Editar i Reenviar (#${selectedEnviament})` : "Nou Enviament (Sense desafiament)") : "Codi Entregat"}</h4>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {isEditable && (
                                    <>
                                        <Button
                                            type="dashed"
                                            icon={<PlusOutlined />}
                                            onClick={handleCreateNewSubmission}
                                        >
                                            Nou Enviament
                                        </Button>
                                        <Button
                                            icon={<FileTextOutlined />}
                                            onClick={() => setShowEnunciatModal(true)}
                                        >
                                            Veure Enunciat
                                        </Button>
                                        <Button
                                            icon={<EyeOutlined />}
                                            onClick={() => setShowTestsModal(true)}
                                        >
                                            Veure Tests
                                        </Button>
                                        <Button
                                            type="primary"
                                            disabled={filesToEdit.length === 0 || loading}
                                            loading={loading}
                                            onClick={handleSend}
                                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                        >
                                            Reenviar
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>

                        <div style={{ marginBottom: 10, padding: 10, background: selectedEnviament ? '#f5f5f5' : '#e6f7ff', borderRadius: 8, border: selectedEnviament ? '1px solid #e8e8e8' : '1px solid #91d5ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <Text strong>Mode: </Text>
                                {selectedEnviament ? (
                                    <Tag color="blue">Reenviament d'entrega #{selectedEnviament}</Tag>
                                ) : (
                                    <Tag color="green">Nou enviament independent (Origen 0 - Web)</Tag>
                                )}
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {selectedEnviament 
                                        ? "S'han carregat els fitxers de l'entrega seleccionada. Pots editar-ne el contingut o crear-ne un de nou." 
                                        : "Pots donar nom a la teva classe .java, editar el codi i prémer Reenviar per avaluar-lo."}
                                </Text>
                            </div>
                            {isEditable && (
                                <Space size="small">
                                    <Button
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={() => {
                                            const curr = filesToEdit.find(f => f.uid === activeFileKey);
                                            if (curr) {
                                                setNewFileNameInput(curr.nom.replace(/\.java$/, ""));
                                                setRenamingUid(curr.uid);
                                                setRenameModalVisible(true);
                                            }
                                        }}
                                    >
                                        Canviar nom classe
                                    </Button>
                                    <Button
                                        size="small"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            setNewFileNameInput("");
                                            setAddFileModalVisible(true);
                                        }}
                                    >
                                        + Classe
                                    </Button>
                                </Space>
                            )}
                        </div>

                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Tabs
                                activeKey={activeFileKey}
                                onChange={setActiveFileKey}
                                type="card"
                                items={filesToEdit.map(file => ({
                                    key: file.uid,
                                    label: (
                                        <span>
                                            {file.nom}
                                            {isEditable && filesToEdit.length > 1 && (
                                                <Popconfirm
                                                    title="Eliminar aquest fitxer?"
                                                    onConfirm={(e) => {
                                                        e?.stopPropagation();
                                                        const remaining = filesToEdit.filter(f => f.uid !== file.uid);
                                                        setFilesToEdit(remaining);
                                                        if (activeFileKey === file.uid && remaining.length > 0) {
                                                            setActiveFileKey(remaining[0].uid);
                                                        }
                                                    }}
                                                    onCancel={(e) => e?.stopPropagation()}
                                                    okText="Sí"
                                                    cancelText="No"
                                                >
                                                    <DeleteOutlined
                                                        style={{ marginLeft: 8, color: '#ff4d4f' }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </Popconfirm>
                                            )}
                                        </span>
                                    ),
                                    children: (
                                        <div style={{ height: '500px', border: '1px solid #d9d9d9' }}>
                                            <Editor
                                                height="100%"
                                                defaultLanguage="java"
                                                value={file.content}
                                                theme="light"
                                                onChange={(val) => handleEditorChange(val, file.uid)}
                                                options={{
                                                    minimap: { enabled: false },
                                                    readOnly: !isEditable
                                                }}
                                            />
                                        </div>
                                    )
                                }))}
                            />
                        </div>
                        {errorOutput && (
                            <div style={{ marginTop: '10px' }}>
                                <Alert
                                    message="Error de Compilació / Execució"
                                    description={<pre style={{ margin: 0, whiteSpace: 'pre-wrap', maxHeight: '150px', overflow: 'auto' }}>{errorOutput}</pre>}
                                    type="error"
                                    showIcon
                                />
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Modal Canviar nom de classe */}
            <Modal
                title="Canviar el nom de la classe Java"
                open={renameModalVisible}
                onOk={handleConfirmRename}
                onCancel={() => setRenameModalVisible(false)}
                okText="Desar"
                cancelText="Cancel·lar"
            >
                <div style={{ marginTop: 15 }}>
                    <Text>Nom de la classe (sense .java):</Text>
                    <Input
                        style={{ marginTop: 8 }}
                        placeholder="Ex: Main, Solucio, Calculadora"
                        value={newFileNameInput}
                        onChange={(e) => setNewFileNameInput(e.target.value)}
                        onPressEnter={handleConfirmRename}
                        autoFocus
                    />
                </div>
            </Modal>

            {/* Modal Afegir nova classe */}
            <Modal
                title="Afegir una nova classe Java"
                open={addFileModalVisible}
                onOk={handleConfirmAddFile}
                onCancel={() => setAddFileModalVisible(false)}
                okText="Crear"
                cancelText="Cancel·lar"
            >
                <div style={{ marginTop: 15 }}>
                    <Text>Nom de la nova classe (sense .java):</Text>
                    <Input
                        style={{ marginTop: 8 }}
                        placeholder="Ex: Helper, Persona, Node"
                        value={newFileNameInput}
                        onChange={(e) => setNewFileNameInput(e.target.value)}
                        onPressEnter={handleConfirmAddFile}
                        autoFocus
                    />
                </div>
            </Modal>
            <TestsListModal
                id_problema={props.element.id}
                nom={props.element.nom}
                id_activitat={props.element.id_practica}
                visible={showTestsModal}
                onClose={setShowTestsModal}
                isMulti={props.element.multi === 1}
            />
            <Modal
                title="Enunciat i Exemples"
                open={showEnunciatModal}
                onCancel={() => setShowEnunciatModal(false)}
                footer={null}
                width={1000}
                style={{ top: 20 }}
            >
                <div style={{ maxHeight: '80vh', overflowY: 'auto', padding: '10px' }}>
                    {props.element && (
                        <>
                            <ProblemaIdEnunciat propIdProblema={props.element.id?.toString()} propIdActivitat={props.element.id_practica?.toString()} />
                            <hr style={{ margin: '30px 0' }} />
                            <ProblemaIdExemples propIdProblema={props.element.id?.toString()} propIdActivitat={props.element.id_practica?.toString()} />
                        </>
                    )}
                </div>
            </Modal>
        </ConfigProvider>
    );
};

export default RevisioModal;
